import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, User, Volume2, Coins, KeyRound, Trash2, Check, Play, Info, Languages
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { LANGUAGES } from "../lib/i18n";
import AdminModelConfig from "../components/app/AdminModelConfig";
import AdminUsersPanel from "../components/app/AdminUsersPanel";
import AdminSitePauseToggle from "../components/app/AdminSitePauseToggle";

const VOICES = [
  { id: "alloy",   note: "Neutral, balanced" },
  { id: "ash",     note: "Clear, articulate" },
  { id: "coral",   note: "Warm, friendly" },
  { id: "echo",    note: "Smooth, calm" },
  { id: "fable",   note: "Expressive" },
  { id: "nova",    note: "Energetic (default)" },
  { id: "onyx",    note: "Deep, authoritative" },
  { id: "sage",    note: "Measured, wise" },
  { id: "shimmer", note: "Bright, cheerful" },
];

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const nav = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [voice, setVoice] = useState(user?.voice || "nova");
  const [savingProfile, setSavingProfile] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState(null);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [transactions, setTransactions] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletePw, setDeletePw] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setVoice(user.voice || "nova");
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/transactions");
        setTransactions(data);
      } catch { /* ignore */ }
    })();
  }, []);

  if (!user) return null;

  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "—";

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch("/auth/me", { name: name.trim(), voice });
      await refresh();
      toast.success(t("profile.updated"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const previewVoice = async (v) => {
    if (previewingVoice) return;
    setPreviewingVoice(v);
    try {
      const res = await api.post(
        "/audio/speak",
        { text: `Hi. This is the ${v} voice.`, voice: v },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); setPreviewingVoice(null); };
      audio.onerror = () => { URL.revokeObjectURL(url); setPreviewingVoice(null); };
      await audio.play();
    } catch (err) {
      toast.error(t("profile.voicePreviewFail"));
      setPreviewingVoice(null);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error(t("profile.pwShort"));
      return;
    }
    if (newPw !== confirmPw) {
      toast.error(t("profile.pwMismatch"));
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      toast.success(t("profile.pwUpdated"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingPw(false);
    }
  };

  const isGoogleOnly = user.auth_provider === "google";

  const doDelete = async () => {
    if (!isGoogleOnly && !deletePw) {
      toast.error(t("profile.enterPw"));
      return;
    }
    setDeleting(true);
    try {
      await api.post("/auth/delete-account", { password: deletePw });
      toast.success(t("profile.accountDeleted"));
      localStorage.removeItem("pq_token");
      window.location.href = "/login";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="profile-page">
      <div className="glass-header sticky top-0 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 h-20">
          <button onClick={() => nav("/")} className="btn-ghost focus-ring" data-testid="profile-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-[0.15em]">{t("profile.back")}</span>
          </button>
          <div className="label-eyebrow">{t("profile.eyebrow")}</div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-14 space-y-14">

        {/* Header block */}
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center">
            <span className="font-display font-black text-2xl tracking-tight">
              {(user.name || user.email || "?")[0].toUpperCase()}
            </span>
          </div>
          <div>
            <div className="font-display text-3xl font-black tracking-tighter" data-testid="profile-name-heading">
              {user.name || user.email.split("@")[0]}
            </div>
            <div className="text-[13px] text-white/50 font-mono-p" data-testid="profile-email">{user.email}</div>
            <div className="label-eyebrow mt-1">{t("profile.memberSince", { date: memberSince })}</div>
          </div>
        </div>

        {/* Coin balance card */}
        <SectionCard>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Coins className="w-5 h-5 text-[#F5C542]" />
              <div>
                <div className="label-eyebrow">{t("profile.balance")}</div>
                <div className="font-display text-3xl font-black tracking-tighter">
                  <span data-testid="profile-credits">{user.credits}</span>
                  <span className="text-white/40 text-xl ml-1">{t("nav.coins")}</span>
                </div>
              </div>
            </div>
            <Link to="/pricing" className="btn-pill focus-ring" data-testid="profile-buy-coins">
              {t("profile.buyCoins")}
            </Link>
          </div>
        </SectionCard>

        {/* Profile form */}
        <SectionCard>
          <SectionHeading icon={User}>{t("profile.general")}</SectionHeading>
          <form onSubmit={saveProfile} className="space-y-5" data-testid="profile-form">
            <Field label={t("profile.displayName")} htmlFor="p-name">
              <input
                id="p-name"
                data-testid="profile-name-input"
                type="text"
                value={name}
                maxLength={60}
                onChange={(e) => setName(e.target.value)}
                className="cmd-input w-full h-11 px-4 rounded-md text-[14px]"
              />
            </Field>
            <Field label={t("profile.email")} htmlFor="p-email">
              <input
                id="p-email"
                value={user.email}
                disabled
                className="cmd-input w-full h-11 px-4 rounded-md text-[14px] opacity-60"
              />
              <div className="label-eyebrow mt-1 opacity-60">{t("profile.emailReadonly")}</div>
            </Field>

            <div>
              <label className="label-eyebrow block mb-2 flex items-center gap-2">
                <Languages className="w-3.5 h-3.5" /> {t("profile.language")}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="profile-language-grid">
                {LANGUAGES.map((l) => {
                  const active = lang === l.code;
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => setLang(l.code)}
                      className={`p-3 rounded-md border cursor-pointer transition-colors text-left ${
                        active
                          ? "border-white/40 bg-white/[0.05]"
                          : "border-white/10 bg-white/[0.02] hover:border-white/20"
                      }`}
                      data-testid={`profile-lang-${l.code}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-display font-black text-[13px]">{l.native}</span>
                        {active && <Check className="w-3.5 h-3.5 text-white/80" />}
                      </div>
                      <div className="text-[11px] text-white/50 mt-1 uppercase tracking-wider">{l.code}</div>
                    </button>
                  );
                })}
              </div>
              <div className="label-eyebrow mt-2 opacity-60">
                {t("profile.languageHelp")}
              </div>
            </div>

            <div>
              <label className="label-eyebrow block mb-2 flex items-center gap-2">
                <Volume2 className="w-3.5 h-3.5" /> {t("profile.preferredVoice")}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="voice-grid">
                {VOICES.map((v) => {
                  const active = voice === v.id;
                  return (
                    <div
                      key={v.id}
                      onClick={() => setVoice(v.id)}
                      className={`p-3 rounded-md border cursor-pointer transition-colors ${
                        active
                          ? "border-white/40 bg-white/[0.05]"
                          : "border-white/10 bg-white/[0.02] hover:border-white/20"
                      }`}
                      data-testid={`voice-${v.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-display font-black text-[13px] capitalize">{v.id}</span>
                        {active && <Check className="w-3.5 h-3.5 text-white/80" />}
                      </div>
                      <div className="text-[11px] text-white/50 mt-1">{v.note}</div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); previewVoice(v.id); }}
                        disabled={previewingVoice === v.id}
                        className="btn-ghost !h-7 !px-2 mt-2 focus-ring"
                        data-testid={`preview-voice-${v.id}`}
                      >
                        {previewingVoice === v.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Play className="w-3 h-3" />
                        )}
                        <span className="text-[10px]">{t("profile.previewVoice")}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <button type="submit" disabled={savingProfile} className="btn-pill focus-ring" data-testid="profile-save-btn">
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : t("profile.save")}
            </button>
          </form>
        </SectionCard>

        {/* Password change — hidden for Google-only accounts */}
        {user.auth_provider !== "google" && (
          <SectionCard>
            <SectionHeading icon={KeyRound}>{t("profile.changePw")}</SectionHeading>
            <form onSubmit={changePw} className="space-y-5" data-testid="password-form">
            <Field label={t("profile.currentPw")} htmlFor="pw-current">
              <input
                id="pw-current"
                type="password"
                required
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                autoComplete="current-password"
                className="cmd-input w-full h-11 px-4 rounded-md text-[14px]"
                data-testid="pw-current-input"
              />
            </Field>
            <Field label={t("profile.newPw")} htmlFor="pw-new">
              <input
                id="pw-new"
                type="password"
                required
                minLength={6}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password"
                className="cmd-input w-full h-11 px-4 rounded-md text-[14px]"
                data-testid="pw-new-input"
              />
            </Field>
            <Field label={t("profile.confirmPw")} htmlFor="pw-confirm">
              <input
                id="pw-confirm"
                type="password"
                required
                minLength={6}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                autoComplete="new-password"
                className={`cmd-input w-full h-11 px-4 rounded-md text-[14px] ${
                  confirmPw && newPw !== confirmPw ? "!border-[#ff9c85]/60" : ""
                }`}
                data-testid="pw-confirm-input"
              />
              {confirmPw && newPw !== confirmPw && (
                <div
                  className="mt-2 text-[12px] font-mono-p text-[#ff9c85]"
                  data-testid="pw-mismatch-error"
                >
                  {t("profile.pwMismatch")}
                </div>
              )}
            </Field>
            <button
              type="submit"
              disabled={savingPw || !newPw || newPw !== confirmPw}
              className="btn-pill focus-ring disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pw-save-btn"
            >
              {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : t("profile.updatePw")}
            </button>
          </form>
        </SectionCard>
        )}

        {/* Transactions */}
        <SectionCard>
          <SectionHeading icon={Coins}>{t("profile.purchases")}</SectionHeading>
          {transactions.length === 0 ? (
            <div className="label-eyebrow opacity-60" data-testid="tx-empty">
              {t("profile.noPurchases")}
            </div>
          ) : (
            <ul className="space-y-2 text-[13px]" data-testid="tx-list">
              {transactions.map((tx) => (
                <li key={tx.session_id} className="flex items-baseline justify-between p-3 rounded-md border border-white/10 bg-white/[0.02]">
                  <div>
                    <div className="text-white/85">{tx.lookup_key === "coins_mini" ? t("profile.miniPack") : t("profile.valuePack")} · +{tx.credits} {t("nav.coins")}</div>
                    <div className="label-eyebrow mt-0.5">{new Date(tx.updated_at).toLocaleString()}</div>
                  </div>
                  <div className="font-mono-p text-white/60">${(tx.amount / 100).toFixed(2)}</div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* Admin — visible only to whitelisted admin */}
        {user.is_admin && (
          <SectionCard>
            <AdminSitePauseToggle />
          </SectionCard>
        )}

        {user.is_admin && (
          <SectionCard>
            <AdminModelConfig />
          </SectionCard>
        )}

        {user.is_admin && (
          <SectionCard>
            <AdminUsersPanel currentAdminId={user.id} />
          </SectionCard>
        )}

        {/* Dangerous */}
        <SectionCard danger>
          <SectionHeading icon={Trash2} danger>{t("profile.danger")}</SectionHeading>
          {!confirmDelete ? (
            <div className="flex items-center justify-between gap-4">
              <p className="text-[13px] text-white/60 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                {t("profile.deleteBody")}
              </p>
              <button
                onClick={() => setConfirmDelete(true)}
                className="btn-ghost focus-ring border-[#ff5a5a]/40 text-[#ff9c85]"
                data-testid="delete-account-btn"
              >
                {t("profile.deleteBtn")}
              </button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="delete-confirm-block">
              <p className="text-[13px] text-[#ffb097]">
                {isGoogleOnly
                  ? t("profile.deleteConfirmGoogle")
                  : t("profile.deleteConfirmPw")}
              </p>
              {!isGoogleOnly && (
                <input
                  type="password"
                  value={deletePw}
                  onChange={(e) => setDeletePw(e.target.value)}
                  placeholder={t("login.password")}
                  className="cmd-input w-full h-11 px-4 rounded-md text-[14px]"
                  data-testid="delete-password-input"
                />
              )}
              <div className="flex items-center gap-3">
                <button
                  onClick={doDelete}
                  disabled={deleting}
                  className="btn-pill focus-ring !bg-[#ff5a5a] !text-white"
                  data-testid="delete-confirm-btn"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("profile.deleteFinal")}
                </button>
                <button
                  onClick={() => { setConfirmDelete(false); setDeletePw(""); }}
                  className="btn-ghost focus-ring"
                  data-testid="delete-cancel-btn"
                >
                  {t("profile.cancel")}
                </button>
              </div>
            </div>
          )}
        </SectionCard>

        <div className="pt-4">
          <button onClick={logout} className="btn-ghost focus-ring" data-testid="profile-logout-btn">
            {t("profile.signOut")}
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ children, danger = false }) {
  return (
    <div
      className={`p-6 rounded-lg border ${
        danger ? "border-[#ff5a5a]/25 bg-[#ff5a5a]/[0.03]" : "border-white/10 bg-black/40"
      }`}
    >
      {children}
    </div>
  );
}

function SectionHeading({ icon: Icon, danger, children }) {
  return (
    <div className="flex items-center gap-2 mb-5">
      {Icon && <Icon className={`w-4 h-4 ${danger ? "text-[#ff9c85]" : "text-white/70"}`} />}
      <h2 className={`label-eyebrow ${danger ? "text-[#ff9c85]" : "text-white/80"}`}>{children}</h2>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="label-eyebrow block mb-2">{label}</label>
      {children}
    </div>
  );
}
